import React, { useState } from 'react';

import { Box, Step, StepLabel, Stepper } from '@mui/material';

import type { BulkClaimResult, ClaimCampaignContract, ClaimCsvRow } from '~/services/LuxLoyalty';

import BulkUploadStep from './steps/BulkUploadStep';
import ResultStep from './steps/ResultStep';
import ReviewStep from './steps/ReviewStep';
import SelectMethodStep from './steps/SelectMethodStep';
import SingleClaimStep from './steps/SingleClaimStep';

type ClaimMethod = 'bulk' | 'single';

const STEPS = ['Select Method', 'Configure', 'Review', 'Result'];

export default function ClaimsWizard() {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedMethod, setSelectedMethod] = useState<ClaimMethod | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<ClaimCampaignContract | null>(null);
  const [rows, setRows] = useState<Array<ClaimCsvRow>>([]);
  const [enrolMember, setEnrolMember] = useState(false);
  const [submitResult, setSubmitResult] = useState<BulkClaimResult | null>(null);

  const handleProceedToReview = (csvRows: Array<ClaimCsvRow>, options?: { enrolMember?: boolean }) => {
    setRows(csvRows);
    setEnrolMember(options?.enrolMember ?? false);
    setActiveStep(2);
  };

  const handleSubmitSuccess = (response: BulkClaimResult) => {
    setSubmitResult(response);
    setActiveStep(3);
  };

  const handleStartNew = () => {
    setActiveStep(0);
    setSelectedMethod(null);
    setSelectedCampaign(null);
    setRows([]);
    setEnrolMember(false);
    setSubmitResult(null);
  };

  return (
    <Box>
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <SelectMethodStep
          selectedMethod={selectedMethod}
          selectedCampaign={selectedCampaign}
          onSelectMethod={setSelectedMethod}
          onSelectCampaign={setSelectedCampaign}
          onNext={() => setActiveStep(1)}
        />
      )}

      {activeStep === 1 && selectedMethod === 'bulk' && selectedCampaign && (
        <BulkUploadStep
          campaign={selectedCampaign}
          onBack={() => setActiveStep(0)}
          onProceedToReview={handleProceedToReview}
        />
      )}

      {activeStep === 1 && selectedMethod === 'single' && selectedCampaign && (
        <SingleClaimStep
          campaign={selectedCampaign}
          onBack={() => setActiveStep(0)}
          onProceedToReview={handleProceedToReview}
        />
      )}

      {activeStep === 2 && selectedCampaign && (
        <ReviewStep
          rows={rows}
          campaign={selectedCampaign}
          enrolMember={enrolMember}
          onBack={() => setActiveStep(1)}
          onSubmitSuccess={handleSubmitSuccess}
        />
      )}

      {activeStep === 3 && submitResult && <ResultStep result={submitResult} onStartNew={handleStartNew} />}
    </Box>
  );
}
